import React, { PureComponent, ReactNode, ReactText, Key } from 'react';
import { EventDataNode, DataNode } from 'rc-tree/lib/interface';
import Tree from 'src/common/Tree';
import { createSelector } from 'reselect';
import { IInputTreeNode, NodesState } from './types';
import {
  toCheckedKeys,
  filterExactCheckedKeys,
  toTwoWayTreeNode,
  toNodesState,
  unzipNodesState,
  zipNodesState,
  unzipNodesStateStrictly,
  zipNodesStateStrictly,
  filterTreeData,
  filterExpandedKeys,
  filterCheckedNodesState,
  uncheckNode,
  uncheckChildNodes,
  getCheckedParentKeys,
  getHalfChecked
} from './utils';
import styles from './index.module.scss';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { trackMPEditAddCPVCode, trackMPEditAddRegion } from 'src/segment/events';
import { MpCodeTypes } from 'src/models/matchingProfiles/types';

export function toTreeData(nodes: readonly IInputTreeNode[], searchPhrase?: string, showKey?: boolean): DataNode[] {
  function wrapMatchedWithSpan(regex: RegExp, label: string): ReactNode {
    return regex ? (
      <span
        dangerouslySetInnerHTML={{
          __html: label.replace(
            regex,
            `<span
          class="${styles.highlightedText}"
          >$1</span>`
          )
        }}
      />
    ) : (
      label
    );
  }

  function handleNode<T extends IInputTreeNode>(node: T): DataNode {
    const { key, label, children, disabled } = node;
    let regex: RegExp;
    if (searchPhrase) {
      regex = new RegExp(`(${searchPhrase})`, 'ig');
    }
    return {
      key,
      disableCheckbox: !!disabled,
      title: () => {
        return (
          <span className={styles.wrap}>
            <span className={styles.label} title={label}>
              {wrapMatchedWithSpan(regex, label)}
            </span>
            {showKey && <span className={styles.key}>{wrapMatchedWithSpan(regex, key.toString())}</span>}
          </span>
        );
      },
      style: node.hidden ? { display: 'none' } : undefined,
      children: children.map(handleNode)
    };
  }
  return nodes.map(handleNode);
}
export interface ITreeProps {
  height?: number;
  tree: readonly IInputTreeNode[];
  nodesState: NodesState;
  onCheck: (nodesState: NodesState) => void;
  disabled: boolean;
  className?: string;
  searchPhrase?: string;
  checkStrictly: boolean;
  showKey?: boolean;
  type: MpCodeTypes;
  mp?: { id: string; name: string };
}

export interface ITreeState {
  expandedKeys: string[];
  collapsedKeys: string[];
}

const getNodesState = (props: ITreeProps): NodesState => props.nodesState;
const getTree = (props: ITreeProps): readonly IInputTreeNode[] => props.tree;

export class MpTree extends PureComponent<ITreeProps, ITreeState> {
  state: ITreeState = {
    expandedKeys: [],
    collapsedKeys: []
  };

  public render(): ReactNode {
    const { height, disabled, className, checkStrictly } = this.props;
    const treeData = this.treeDataSelector(this.props);

    const checkedKeys = !checkStrictly
      ? this.checkedKeysSelector(this.props)
      : {
          checked: this.strictlyCheckedKeysSelector(this.props),
          halfChecked: this.halfCheckedKeysSelector(this.props)
        };
    const expandedKeys = this.expandedKeysSelector(this.props, this.state);
    return (
      <Tree
        treeData={treeData}
        height={height}
        expandedKeys={expandedKeys}
        onExpand={this.onManualExpand}
        checkedKeys={checkedKeys}
        onCheck={this.onRegularCheck}
        onSelect={this.onRegularSelect}
        checkable
        disabled={disabled}
        className={classNames(className, styles.mpTree)}
        checkStrictly={checkStrictly}
        blockNode
        switcherIcon={<FontAwesomeIcon icon={faChevronRight} />}
      />
    );
  }

  // FIXME: need to refactor this place bc of redux and reselect are deprecated, will be removed soon
  private searchTreeDataSelector = createSelector(
    getTree,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props: any) => props,
    (tree, props) => (props.searchPhrase ? filterTreeData(tree, props.searchPhrase, !!props.showKey) : tree)
  );
  private treeDataSelector = createSelector([this.searchTreeDataSelector], tree =>
    toTreeData(tree, this.props.searchPhrase, this.props.showKey)
  );
  private twoWayTreeSelector = createSelector([this.treeDataSelector], tree => toTwoWayTreeNode(tree));

  private disabledKeysSelector = createSelector([this.treeDataSelector, getNodesState], tree => {
    const disabledKeys: string[] = [];
    const handleNode = (node: DataNode): void => {
      if (node.disableCheckbox) {
        disabledKeys.push(node.key.toString());
      }
      node.children?.forEach(handleNode);
    };
    tree.forEach(handleNode);
    return disabledKeys;
  });

  private unzippedNodesState = createSelector(
    [this.twoWayTreeSelector, getNodesState, this.disabledKeysSelector],
    (tree, state, disabledKeys) => unzipNodesState(tree, filterCheckedNodesState(state), disabledKeys)
  );
  private strictlyNodesState = createSelector(
    [this.twoWayTreeSelector, getNodesState, this.disabledKeysSelector],
    (tree, state, disabledKeys) => {
      return unzipNodesStateStrictly(tree, state, disabledKeys);
    }
  );
  private expandedKeysSelector = createSelector(
    this.searchTreeDataSelector,
    (props: ITreeProps, state: ITreeState) => {
      return { expandedKeys: state.expandedKeys, collapsedKeys: state.collapsedKeys };
    },
    filterExpandedKeys
  );

  private checkedKeysSelector = createSelector([this.unzippedNodesState], nodesState => toCheckedKeys(nodesState));
  private strictlyCheckedKeysSelector = createSelector([this.strictlyNodesState], nodesState =>
    toCheckedKeys(nodesState)
  );
  private exactCheckedKeysSelector = createSelector([getNodesState], nodesState => filterExactCheckedKeys(nodesState));

  private halfCheckedSelector = createSelector([this.twoWayTreeSelector, getNodesState], (tree, state) => {
    return getHalfChecked(tree, state);
  });
  private halfCheckedKeysSelector = createSelector([this.halfCheckedSelector], halfChecked => {
    return Array.from(halfChecked).map(x => x.key);
  });

  private onCheckWithZip = (nodesState: NodesState): void => {
    const tree = this.twoWayTreeSelector(this.props);
    const state = !this.props.checkStrictly ? zipNodesState(tree, nodesState) : zipNodesStateStrictly(tree, nodesState);
    return this.props.onCheck(state);
  };

  private readonly handleCheckStrictly = (checkedKeys: string[], nodeKey: string, nodeChecked: boolean): void => {
    const tree = this.twoWayTreeSelector(this.props);
    const filteredChecked = uncheckChildNodes(
      tree,
      nodeKey,
      checkedKeys.map(key => key.toString())
    );
    //Remove unchecked key from current exact checked keys
    const exactCheckedKeys = this.exactCheckedKeysSelector(this.props).filter(key => filteredChecked.includes(key));
    //Set checked parent nodes to ExctactChecked if unchecking a node
    const nextExactCheckedKeys = !nodeChecked
      ? Array.from(new Set([...exactCheckedKeys, ...getCheckedParentKeys(tree, nodeKey, filteredChecked)]))
      : exactCheckedKeys;
    this.onCheckWithZip(toNodesState(filteredChecked, nextExactCheckedKeys));
  };

  private readonly handleCheck = (checkedKeys: string[], nodeKey: string, nodeChecked: boolean): void => {
    const disabled = this.disabledKeysSelector(this.props).includes(nodeKey);
    const expanded = this.expandedKeysSelector(this.props, this.state).includes(nodeKey);
    if (disabled) {
      this.onToggle(nodeKey, !expanded);
      return;
    }
    if (nodeChecked) {
      this.onToggle(nodeKey, true);
    }
    if (!this.props.checkStrictly) {
      this.onCheckWithZip(
        toNodesState(
          checkedKeys.map(key => key.toString()),
          []
        )
      );
    } else {
      this.handleCheckStrictly(checkedKeys, nodeKey, nodeChecked);
    }
  };

  private readonly handleSelect = (nodeKey: string): void => {
    const currentlyCheckedKeys = this.checkedKeysSelector(this.props);
    const hasChecked = !!currentlyCheckedKeys.find(key => key === nodeKey);
    if (hasChecked) {
      const tree = this.twoWayTreeSelector(this.props);
      const nextCheckedKeys = uncheckNode(tree, nodeKey, currentlyCheckedKeys);
      this.handleCheck(nextCheckedKeys, nodeKey, false);
    } else {
      this.handleCheck([...currentlyCheckedKeys, nodeKey], nodeKey, true);
    }
  };

  private readonly handleSelectStrictly = (nodeKey: string): void => {
    const currentlyCheckedKeys = this.strictlyCheckedKeysSelector(this.props);
    const hasChecked = !!currentlyCheckedKeys.find(key => key === nodeKey);
    if (hasChecked) {
      this.handleCheck(
        currentlyCheckedKeys.filter(key => key !== nodeKey),
        nodeKey,
        false
      );
    } else {
      this.handleCheck([...currentlyCheckedKeys, nodeKey], nodeKey, true);
    }
  };

  private readonly onRegularSelect = (
    selectedKeys: Key[],
    info: {
      event: 'select';
      selected: boolean;
      node: EventDataNode;
      selectedNodes: DataNode[];
      nativeEvent: MouseEvent;
    }
  ): void => {
    const nodeKey = info.node.key.toString();
    if (!this.props.checkStrictly) {
      this.handleSelect(nodeKey);
    } else {
      this.handleSelectStrictly(nodeKey);
    }
  };

  private readonly onRegularCheck = (
    checkedKeys: ReactText[] | { checked: ReactText[] },
    info: {
      checked: boolean;
      checkedNodes: DataNode[];
      event: string;
      nativeEvent: MouseEvent;
      node: EventDataNode;
    }
  ): void => {
    const checked = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked;
    this.handleCheck(
      checked.map(key => key.toString()),
      info.node.key.toString(),
      info.checked
    );

    if (this.props.mp) {
      const mp = this.props.mp;
      /** @todo should be moved out to MpTree-references (aka where it used) */
      if (this.props.type === 'cpvCodes') {
        trackMPEditAddCPVCode({ id: mp.id, name: mp.name }, info.checked, info.node.key.toString());
      } else if (this.props.type === 'nutsCodes') {
        trackMPEditAddRegion({ id: mp.id, name: mp.name }, info.checked, info.node.key.toString());
      }
    }
  };

  private onManualExpand = (
    expandedKeys: Key[],
    info: {
      node: EventDataNode;
      expanded: boolean;
      nativeEvent: MouseEvent;
    }
  ): void => {
    this.onToggle(info.node.key.toString(), info.expanded);
  };

  private onToggle = (nodeKey: string, expanded: boolean): void => {
    if (expanded) {
      this.setState({
        expandedKeys: [...this.state.expandedKeys, nodeKey],
        collapsedKeys: this.state.collapsedKeys.filter(x => x !== nodeKey)
      });
    } else {
      this.setState({
        expandedKeys: this.state.expandedKeys.filter(x => x !== nodeKey),
        collapsedKeys: [...this.state.collapsedKeys, nodeKey]
      });
    }
  };
}

export default MpTree;
